import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  CheckIcon,
  DocumentIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../../components/Notification';
import tprmService from '../../../services/compliance/tprm.service';
import {
  EvidenceStatus,
  EvidenceType,
  VendorDocument,
  VendorEvidence,
  VendorEvidenceCreate,
  VendorEvidenceUpdate,
} from '../../../types/compliance';
import ModalHeader from '../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../hooks/useModalDrag';
import { saveAs } from '../../../utils/saveFile';

interface Props {
  vendorId: string;
}

const TYPE_LABELS: Record<EvidenceType, string> = {
  soc2: 'SOC 2',
  iso27001: 'ISO 27001',
  insurance: 'Insurance',
  dpa: 'DPA',
  pen_test: 'Pen Test',
  bcp_dr: 'BCP / DR',
  security_questionnaire: 'Security Questionnaire',
  other: 'Other',
};

const TYPE_BADGE: Record<EvidenceType, string> = {
  soc2: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  iso27001: 'bg-purple-200 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  insurance: 'bg-teal-200 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  dpa: 'bg-indigo-200 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  pen_test: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  bcp_dr: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  security_questionnaire: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  other: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const STATUS_LABELS: Record<EvidenceStatus, string> = {
  current: 'Current',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
  not_received: 'Not Received',
  not_collected: 'Not Collected',
};

const STATUS_BADGE: Record<EvidenceStatus, string> = {
  current: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  expiring_soon: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  expired: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  not_received: 'bg-orange-200 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  not_collected: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

type FormState = {
  evidence_type: EvidenceType;
  required: boolean;
  received_date: string;
  expiry_date: string;
  notes: string;
};

const emptyForm: FormState = {
  evidence_type: 'soc2',
  required: false,
  received_date: '',
  expiry_date: '',
  notes: '',
};

function rowToForm(e: VendorEvidence): FormState {
  return {
    evidence_type: e.evidence_type,
    required: e.required,
    received_date: e.received_date || '',
    expiry_date: e.expiry_date || '',
    notes: e.notes || '',
  };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString();
  } catch {
    return iso;
  }
}

function truncate(text: string, n = 60): string {
  if (!text) return '';
  if (text.length <= n) return text;
  return text.slice(0, n) + '…';
}

export default function EvidencePanel({ vendorId }: Props) {
  const [rows, setRows] = useState<VendorEvidence[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(modalOpen);
  const [editing, setEditing] = useState<VendorEvidence | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const uploadTargetRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] =
    useState<{ title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tprmService.listEvidence(vendorId);
      setRows(data);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load evidence', message: msg });
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  // ESC-to-close
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (e: VendorEvidence) => {
    setEditing(e);
    setForm(rowToForm(e));
    setModalOpen(true);
  };

  const handleDelete = async (e: VendorEvidence) => {
    if (!window.confirm(`Delete ${TYPE_LABELS[e.evidence_type]} evidence record?`)) return;
    try {
      await tprmService.deleteEvidence(vendorId, e.id);
      setSuccessNotification({ title: 'Deleted', message: 'Evidence record deleted.' });
      load();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Delete', message: msg });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        const body: VendorEvidenceUpdate = {
          evidence_type: form.evidence_type,
          required: form.required,
          received_date: form.received_date || null,
          expiry_date: form.expiry_date || null,
          notes: form.notes,
        };
        await tprmService.updateEvidence(vendorId, editing.id, body);
        setSuccessNotification({ title: 'Updated', message: 'Evidence record updated.' });
      } else {
        const body: VendorEvidenceCreate = {
          evidence_type: form.evidence_type,
          required: form.required,
          received_date: form.received_date || null,
          expiry_date: form.expiry_date || null,
          notes: form.notes,
        };
        await tprmService.createEvidence(vendorId, body);
        setSuccessNotification({ title: 'Created', message: 'Evidence record created.' });
      }
      setModalOpen(false);
      load();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Save', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const triggerUpload = (evidenceId: string) => {
    uploadTargetRef.current = evidenceId;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    const evidenceId = uploadTargetRef.current;
    // Clear input value early so selecting the same file twice fires change.
    if (fileInputRef.current) fileInputRef.current.value = '';
    uploadTargetRef.current = null;
    if (!file || !evidenceId) return;

    setUploadingId(evidenceId);
    try {
      await tprmService.uploadEvidenceDocument(vendorId, evidenceId, file);
      setSuccessNotification({
        title: 'Uploaded',
        message: `"${file.name}" uploaded successfully.`,
      });
      load();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Upload', message: msg });
    } finally {
      setUploadingId(null);
    }
  };

  const handleDownload = async (doc: VendorDocument) => {
    try {
      await saveAs(doc.original_filename, () =>
        tprmService.downloadVendorDocument(vendorId, doc.id),
      );
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Download', message: msg });
    }
  };

  const handleDeleteDocument = async (doc: VendorDocument) => {
    if (!window.confirm(`Delete document "${doc.original_filename}"?`)) return;
    try {
      await tprmService.deleteVendorDocument(vendorId, doc.id);
      setSuccessNotification({
        title: 'Document Deleted',
        message: `"${doc.original_filename}" deleted.`,
      });
      load();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Delete Document', message: msg });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold">Evidence</h2>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
        >
          <PlusIcon className="w-4 h-4" />
          New Evidence
        </button>
      </div>

      {/* Shared hidden file input, targeted via uploadTargetRef. */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {loading ? (
        <div className="text-center py-6 text-gray-500">Loading evidence...</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 text-gray-500 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          No evidence records yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr className="text-left">
                <th className="py-2 px-3">Type</th>
                <th className="py-2 px-3">Required</th>
                <th className="py-2 px-3">Received</th>
                <th className="py-2 px-3">Expiry</th>
                <th className="py-2 px-3">Document</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Notes</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const uploading = uploadingId === e.id;
                return (
                  <tr
                    key={e.id}
                    className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="py-2 px-3">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${TYPE_BADGE[e.evidence_type]}`}
                      >
                        {TYPE_LABELS[e.evidence_type]}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      {e.required ? (
                        <CheckIcon className="w-4 h-4 text-green-600" />
                      ) : (
                        <XMarkIcon className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      {formatDate(e.received_date)}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      {formatDate(e.expiry_date)}
                    </td>
                    <td className="py-2 px-3">
                      {e.document ? (
                        <div className="inline-flex items-center gap-1.5">
                          <DocumentIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <button
                            type="button"
                            onClick={() => handleDownload(e.document!)}
                            className="text-blue-600 hover:underline dark:text-blue-400 max-w-[180px] truncate text-left"
                            title={e.document.original_filename}
                          >
                            {e.document.original_filename}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteDocument(e.document!)}
                            className="text-red-500 hover:text-red-700 dark:text-red-400"
                            title="Delete document"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => triggerUpload(e.id)}
                          disabled={uploading}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded hover:bg-highlight disabled:opacity-50"
                        >
                          {uploading ? (
                            <>
                              <span className="inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                              Upload
                            </>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap ${STATUS_BADGE[e.status]}`}
                      >
                        {STATUS_LABELS[e.status]}
                      </span>
                    </td>
                    <td className="py-2 px-3 max-w-[240px]">
                      <span
                        className="text-xs text-gray-600 dark:text-gray-300"
                        title={e.notes || ''}
                      >
                        {truncate(e.notes, 60) || '—'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => openEdit(e)}
                          className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(e)}
                          className="p-1 text-red-600 hover:text-red-800 dark:text-red-400"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40">
          <div
            ref={panelRef}
            style={panelStyle}
            className="bg-panel rounded-lg w-full max-w-lg border border-gray-200 dark:border-gray-700"
          >
            <ModalHeader
              title={editing ? 'Edit Evidence' : 'New Evidence'}
              onClose={saving ? undefined : () => setModalOpen(false)}
              dragHandleProps={dragHandleProps}
            />
            <div className="p-6">
            <label className="block text-sm font-medium mb-1">Evidence Type</label>
            <select
              value={form.evidence_type}
              onChange={(e) =>
                setForm((f) => ({ ...f, evidence_type: e.target.value as EvidenceType }))
              }
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 mb-3"
            >
              {(Object.keys(TYPE_LABELS) as EvidenceType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>

            <label className="inline-flex items-center gap-2 text-sm mb-3">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(e) => setForm((f) => ({ ...f, required: e.target.checked }))}
                className="h-4 w-4"
              />
              Required for this vendor
            </label>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Received Date</label>
                <input
                  type="date"
                  value={form.received_date}
                  onChange={(e) => setForm((f) => ({ ...f, received_date: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Expiry Date</label>
                <input
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
                />
              </div>
            </div>

            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              maxLength={5000}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 mb-4"
            />

            {!editing && (
              <p className="text-xs text-gray-500 mb-3">
                You can attach a document after creating this evidence record.
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={saving}
                className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : editing ? 'Save' : 'Create'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}

      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
